import { PASSWORD_MIN_LENGTH, PASSWORD_CHARS } from '../config/constants.js';
import {
  validatePassword as validatePasswordShared,
  PASSWORD_MIN_LENGTH as SHARED_MIN_LENGTH,
} from '../../server/shared/passwordValidation.js';

/**
 * 暗号学的に安全なランダムパスワードを生成
 * @param {number} length - パスワードの長さ（デフォルト: PASSWORD_MIN_LENGTH）
 * @param {Object} options - オプション
 * @param {boolean} options.includeSpecial - 特殊文字を含めるか（デフォルト: true）
 * @returns {string} - 生成されたパスワード
 */
export function generateSecurePassword(length = PASSWORD_MIN_LENGTH, options = {}) {
  const includeSpecial = options.includeSpecial !== false;
  
  // 使用する文字セット
  let chars = PASSWORD_CHARS.LOWERCASE + PASSWORD_CHARS.UPPERCASE + PASSWORD_CHARS.NUMBERS;
  if (includeSpecial) {
    chars += PASSWORD_CHARS.SPECIAL;
  }
  
  // 暗号学的に安全な乱数を使用
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars[array[i] % chars.length];
  }
  
  // パスワードの強度を確保（少なくとも1つずつ含まれるように）
  return ensurePasswordComplexity(password, includeSpecial);
}

/**
 * パスワードに必要な文字種が含まれていることを確認
 * @param {string} password - チェックするパスワード
 * @param {boolean} includeSpecial - 特殊文字を必須とするか
 * @returns {string} - 修正されたパスワード
 */
function ensurePasswordComplexity(password, includeSpecial) {
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = includeSpecial ? /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password) : true;
  
  // すべての条件を満たしている場合はそのまま返す
  if (hasLowercase && hasUppercase && hasNumber && hasSpecial) {
    return password;
  }
  
  // 条件を満たさない場合は再生成
  return generateSecurePassword(password.length, { includeSpecial });
}

/**
 * パスワードの強度をチェック
 * @param {string} password - チェックするパスワード
 * @returns {Object} - { strong: boolean, score: number, feedback: string[] }
 */
export function checkPasswordStrength(password) {
  const feedback = [];
  let score = 0;
  
  // 長さチェック
  if (password.length >= PASSWORD_MIN_LENGTH) {
    score += 1;
  } else {
    feedback.push(`${PASSWORD_MIN_LENGTH}文字以上にしてください`);
  }
  
  // 小文字チェック
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('小文字を含めてください');
  }
  
  // 大文字チェック
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('大文字を含めてください');
  }
  
  // 数字チェック
  if (/[0-9]/.test(password)) {
    score += 1;
  } else {
    feedback.push('数字を含めてください');
  }
  
  // 特殊文字チェック
  if (/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) {
    score += 1;
  } else {
    feedback.push('特殊文字を含めてください');
  }
  
  return {
    strong: score >= 4,
    score,
    feedback
  };
}

/** @see shared/passwordValidation.js */
export function validatePassword(password) {
  const result = validatePasswordShared(password);
  if (!result.valid && result.message?.includes('8文字以上')) {
    return {
      valid: false,
      message: `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください。`,
    };
  }
  return result;
}

export { SHARED_MIN_LENGTH as PASSWORD_VALIDATION_MIN_LENGTH };

